import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tenantApi } from "@/api/chain";
import type { TenantBranding } from "@/api/chain";
import { TenantBrandingFields } from "@/components/chain/TenantBrandingFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  brandingFromRow,
  brandingToPayload,
  emptyBrandingForm,
} from "@/lib/tenantBranding";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

type TenantRow = {
  id: string;
  name: string;
  status: string;
  maxHospitals: number;
  maxUsers: number;
  logoUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  sidebarBgColor?: string | null;
  customUiColors?: boolean;
  usage?: { hospitalCount: number; userCount: number };
  _count?: { hospitals: number; admins: number };
};

const initialCreateForm = () => ({
  tenantName: "",
  maxHospitals: "10",
  maxUsers: "50",
  hospitalName: "",
  hospitalAddress: "",
  hospitalContact: "",
  hospitalEmail: "",
  adminName: "",
  adminEmail: "",
  adminPassword: "",
  branding: emptyBrandingForm(),
});

export default function TenantGroupsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<TenantRow | null>(null);
  const [form, setForm] = useState(initialCreateForm);
  const [editForm, setEditForm] = useState({
    name: "",
    maxHospitals: "10",
    maxUsers: "50",
    branding: emptyBrandingForm(),
  });

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["tenant-groups"],
    queryFn: async () => {
      const r = await tenantApi.listGroups();
      return (r.data.data ?? []) as TenantRow[];
    },
  });

  const createMut = useMutation({
    mutationFn: () =>
      tenantApi.createGroup({
        name: form.tenantName,
        maxHospitals: Number(form.maxHospitals),
        maxUsers: Number(form.maxUsers),
        branding: brandingToPayload(form.branding),
        hospital: {
          name: form.hospitalName,
          address: form.hospitalAddress,
          contactNumber: form.hospitalContact,
          email: form.hospitalEmail,
        },
        chainAdmin: {
          name: form.adminName,
          email: form.adminEmail,
          password: form.adminPassword,
        },
      }),
    onSuccess: () => {
      toast.success("Tenant created");
      setOpen(false);
      setForm(initialCreateForm());
      qc.invalidateQueries({ queryKey: ["tenant-groups"] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      toast.error(e.response?.data?.message ?? "Failed to create tenant");
    },
  });

  const updateMut = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error("No tenant selected");
      return tenantApi.updateGroup(editing.id, {
        name: editForm.name.trim(),
        maxHospitals: Number(editForm.maxHospitals),
        maxUsers: Number(editForm.maxUsers),
        ...brandingToPayload(editForm.branding),
      });
    },
    onSuccess: () => {
      toast.success("Tenant updated");
      setEditOpen(false);
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["tenant-groups"] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      toast.error(e.response?.data?.message ?? "Failed to update tenant");
    },
  });

  const openEdit = (g: TenantRow) => {
    setEditing(g);
    setEditForm({
      name: g.name,
      maxHospitals: String(g.maxHospitals),
      maxUsers: String(g.maxUsers),
      branding: brandingFromRow(g),
    });
    setEditOpen(true);
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Tenants</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Set default logo and colors for each hospital chain. Hospitals can override per site.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>Create tenant</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg">
            <DialogHeader>
              <DialogTitle>New tenant</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tenant name</Label>
                <Input
                  value={form.tenantName}
                  onChange={(e) => setForm((f) => ({ ...f, tenantName: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Max hospitals</Label>
                  <Input
                    type="number"
                    value={form.maxHospitals}
                    onChange={(e) => setForm((f) => ({ ...f, maxHospitals: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Max admin users</Label>
                  <Input
                    type="number"
                    value={form.maxUsers}
                    onChange={(e) => setForm((f) => ({ ...f, maxUsers: e.target.value }))}
                  />
                </div>
              </div>
              <TenantBrandingFields
                value={form.branding}
                onChange={(branding) => setForm((f) => ({ ...f, branding }))}
              />
              <p className="text-sm font-medium text-muted-foreground">First hospital</p>
              {(
                [
                  ["hospitalName", "Name"],
                  ["hospitalAddress", "Address"],
                  ["hospitalContact", "Contact"],
                  ["hospitalEmail", "Email"],
                ] as const
              ).map(([key, label]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <Input
                    value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  />
                </div>
              ))}
              <p className="text-sm font-medium text-muted-foreground">Chain admin</p>
              {(
                [
                  ["adminName", "Name"],
                  ["adminEmail", "Email"],
                  ["adminPassword", "Password"],
                ] as const
              ).map(([key, label]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <Input
                    type={key === "adminPassword" ? "password" : key === "adminEmail" ? "email" : "text"}
                    value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  />
                </div>
              ))}
              <Button className="w-full" disabled={createMut.isPending} onClick={() => createMut.mutate()}>
                Create tenant
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit tenant — {editing?.name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tenant name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Max hospitals</Label>
                  <Input
                    type="number"
                    value={editForm.maxHospitals}
                    onChange={(e) => setEditForm((f) => ({ ...f, maxHospitals: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Max admin users</Label>
                  <Input
                    type="number"
                    value={editForm.maxUsers}
                    onChange={(e) => setEditForm((f) => ({ ...f, maxUsers: e.target.value }))}
                  />
                </div>
              </div>
              <TenantBrandingFields
                value={editForm.branding}
                onChange={(branding: TenantBranding) => setEditForm((f) => ({ ...f, branding }))}
                logoUpload={editing ? { kind: "group", groupId: editing.id } : undefined}
              />
              <Button className="w-full" disabled={updateMut.isPending} onClick={() => updateMut.mutate()}>
                Save changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tenant</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Hospitals</TableHead>
              <TableHead>Admins</TableHead>
              <TableHead>Branding</TableHead>
              <TableHead className="w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((g) => (
              <TableRow key={g.id}>
                <TableCell className="font-medium">{g.name}</TableCell>
                <TableCell>{g.status}</TableCell>
                <TableCell>
                  {g.usage?.hospitalCount ?? g._count?.hospitals ?? 0} / {g.maxHospitals}
                </TableCell>
                <TableCell>
                  {g.usage?.userCount ?? g._count?.admins ?? 0} / {g.maxUsers}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {g.primaryColor && (
                      <span
                        className="inline-block h-4 w-4 rounded border"
                        style={{ backgroundColor: g.primaryColor }}
                        title={g.primaryColor}
                      />
                    )}
                    {g.logoUrl ? (
                      <span className="text-xs text-muted-foreground truncate max-w-[120px]" title={g.logoUrl}>
                        Custom logo
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Default</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(g)} aria-label="Edit tenant">
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
